import ErrorToast from "../../components/common/toasts/ErrorToast";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PremiumBackLink from "./components/PremiumBackLink";
import { useAuth } from "../../context/AuthContext";
import useOrderEditor from "./hooks/useOrderEditor";
import {
  OrderStatus,
  isProcessing,
  statusLabels,
  processingDescriptions,
} from "./constants";
import ProgressBar from "../../components/common/ProgressBar";
import OrderActions from "./components/OrderActions";
import ReviewWarnings from "./components/ReviewWarnings";
import OrderHistory from "./components/OrderHistory";
import DocumentContents from "./components/DocumentContents";
import ReviewForm from "./ReviewForm";
import PremiumLayout from "./components/PremiumLayout";
import { Status, reviewFieldId } from "./components/Fields";
import {
  ReviewIssueContext,
  focusReviewField,
} from "./components/ReviewIssueHint";
import { premiumPaths } from "./navigation";
import OrderSteps, {
  getOrderStep,
  orderStepPaths,
} from "./components/OrderSteps";
import PremiumNotice from "./components/PremiumNotice";
import IssueActions from "./components/IssueActions";
import OrderForm from "./components/orders/OrderForm";

export default function OrderDetail({ step: requestedStep }) {
  const { orderId } = useParams();
  const { accessToken } = useAuth();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const {
    order,
    draft,
    dirty,
    busy: actionBusy,
    error,
    openDocument,
    refresh,
    preview,
    approve,
    retry,
    save,
    onChange,
    setDocumentsDirty,
    documentsUpdated,
  } = useOrderEditor(orderId, accessToken);
  const [documentsBusy, setDocumentsBusy] = useState(false);
  const [selection, setSelection] = useState(null);
  const returnFocus = useRef(null);
  const activeSelection = order?.reviewIssues?.includes(selection?.issue)
    ? selection
    : null;
  useLayoutEffect(() => {
    const id = activeSelection?.id || returnFocus.current;
    if (id) focusReviewField(id);
    returnFocus.current = null;
  }, [activeSelection]);
  const busy = actionBusy || documentsBusy;
  const editable = [OrderStatus.REVIEW, OrderStatus.READ_FAILED].includes(
    order?.status,
  );
  const hasDocuments = order?.documents.length > 0;
  const step = requestedStep || getOrderStep(order?.status);
  useEffect(() => {
    if (
      requestedStep === 4 &&
      order?.status === OrderStatus.REVIEW &&
      order.previewRevision !== order.revision
    )
      navigate(premiumPaths.preview(orderId), { replace: true });
  }, [requestedStep, order, orderId, navigate]);
  const loaded = Boolean(order);
  useEffect(() => {
    if (loaded && hash.startsWith("#premium-")) {
      document.getElementById(hash.slice(1))?.scrollIntoView({
        block: hash === "#premium-recipients" ? "start" : "center",
      });
    }
  }, [loaded, hash]);
  const showSource = (sources, target) => {
    const available = sources.filter((entry) =>
      order.documents.some((doc) => doc.id === entry.documentId),
    );
    const index = Math.max(
      0,
      available.findIndex((entry) => entry.regions?.length),
    );
    if (available[index])
      openDocument(
        premiumPaths.source(orderId, available[index].documentId, {
          ...target,
          source: index,
        }),
      );
  };
  const documentLabels = {
    ...Object.fromEntries(
      (order?.plugin?.requirements.documents || []).map((item) => [
        item.kind,
        item.label,
      ]),
    ),
    ...order?.plugin?.review.pageLabels,
  };
  const companyName = order?.config.exporter.split("\n")[0];
  return (
    <PremiumLayout
      title={
        requestedStep === 1
          ? "Dokumenty źródłowe"
          : step === 4
            ? "Wystaw i pobierz świadectwo"
            : order?.status === OrderStatus.REVIEW
              ? "Sprawdź świadectwo"
              : "Świadectwo Premium"
      }
      subtitle={companyName}
      actions={
        <PremiumBackLink
          destination={order ? "Lista świadectw" : "Konto Premium"}
          to={
            order
              ? premiumPaths.orders(order.organizationId)
              : premiumPaths.home
          }
        />
      }
    >
      {order && (
        <div className="premium-order-meta">
          <div>
            {order.status !== OrderStatus.ISSUED && (
              <strong>
                {order.draft.invoiceNumbers
                  ? `Faktura: ${order.draft.invoiceNumbers}`
                  : "Dokumenty jednej dostawy"}
              </strong>
            )}
          </div>
          <Status status={order.status} />
        </div>
      )}
      <ErrorToast message={error || order?.error} />
      {!order && !error && <p role="status">Ładowanie…</p>}
      {order && (
        <>
          <OrderSteps
            step={step}
            paths={orderStepPaths(order, { dirty })}
            busy={busy}
            onNavigate={(nextStep, path) => openDocument(path, nextStep === 3)}
          />
          {step === 2 && editable && hasDocuments && draft && (
            <ReviewWarnings
              onSelect={(issue) =>
                setSelection({
                  issue,
                  id: reviewFieldId(issue.field, issue.itemId),
                })
              }
              issues={order.reviewIssues}
              warnings={order.warnings}
              items={draft.items || []}
              dirty={dirty}
              busy={busy}
            />
          )}
          {step !== 4 && !(step === 1 && editable) && (
            <section className="card premium-panel">
              <h2>Dokumenty źródłowe</h2>
              <ul className="premium-files">
                {order.documents.map((doc) => (
                  <li key={doc.id}>
                    <span>
                      {doc.name}{" "}
                      <small>· {documentLabels[doc.kind] || doc.kind}</small>
                      <DocumentContents
                        pages={doc.pages}
                        labels={documentLabels}
                      />
                    </span>
                    <button
                      className="btn btn-outline-primary"
                      disabled={busy}
                      onClick={() =>
                        openDocument(
                          premiumPaths.document(orderId, doc.id, step === 1),
                        )
                      }
                    >
                      Otwórz dokument
                    </button>
                  </li>
                ))}
              </ul>
              {editable && (
                <button
                  className="btn btn-outline-primary"
                  disabled={busy}
                  onClick={() =>
                    openDocument(premiumPaths.orderDocuments(orderId))
                  }
                >
                  Zmień dokumenty
                </button>
              )}
            </section>
          )}
          {step === 1 && editable && (
            <OrderForm
              key={`${order.id}:${order.revision}`}
              organization={{
                id: order.organizationId,
                active: true,
                plugin: order.plugin,
              }}
              token={accessToken}
              order={order}
              onCreated={documentsUpdated}
              onContinue={(updated) => documentsUpdated(updated, true)}
              onDirtyChange={setDocumentsDirty}
              onBusyChange={setDocumentsBusy}
              onOpenDocument={(id) =>
                openDocument(premiumPaths.document(orderId, id, true))
              }
            />
          )}
          {step === 1 && !editable && (
            <OrderActions
              busy={busy}
              nextLabel={
                getOrderStep(order.status) === 4
                  ? "Przejdź do wystawienia i pobrania"
                  : "Przejdź dalej"
              }
              onNext={() => openDocument(premiumPaths.order(orderId))}
            />
          )}
          {step !== 1 && isProcessing(order.status) && (
            <div className="card premium-panel" role="status">
              <div className="premium-toolbar">
                <h2>Przetwarzamy zlecenie</h2>
                <button
                  type="button"
                  onClick={refresh}
                  disabled={busy}
                  className="btn btn-outline-secondary btn-sm premium-refresh"
                  aria-label="Odśwież status"
                  title="Odśwież status"
                >
                  <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                </button>
              </div>
              <ProgressBar
                label={statusLabels[order.status]}
                description={processingDescriptions[order.status]}
              />
              <p>
                Możesz wrócić do listy. Nie przerwie to procesu{" "}
                {order.status === OrderStatus.QUEUED
                  ? "odczytu dokumentów."
                  : "wystawiania świadectwa."}
              </p>
            </div>
          )}
          {step !== 1 && order.status.endsWith("failed") && (
            <button
              className="btn btn-primary premium-space-after"
              disabled={busy || dirty || !hasDocuments}
              onClick={retry}
            >
              Ponów{" "}
              {order.status === OrderStatus.READ_FAILED
                ? "odczyt"
                : "wystawienie tego samego świadectwa"}
            </button>
          )}
          {step === 4 && order.status === OrderStatus.ISSUED && (
            <section
              className="card premium-panel"
              aria-labelledby="premium-issued-title"
            >
              <h2 id="premium-issued-title">Świadectwo jest gotowe</h2>
              <p>Zatwierdzone dane są zapisane w historii zlecenia.</p>
              <button
                type="button"
                className="btn btn-primary align-self-start m-0"
                disabled={busy}
                onClick={() => openDocument(premiumPaths.certificate(orderId))}
              >
                Otwórz i pobierz świadectwo
              </button>
            </section>
          )}
          {step === 4 &&
            order.status === OrderStatus.REVIEW &&
            order.previewRevision === order.revision && (
              <IssueActions
                order={order}
                ready
                busy={busy}
                approve={approve}
                save={save}
              />
            )}
          {step === 2 && editable && !hasDocuments && (
            <PremiumNotice warning>
              Dodaj co najmniej jeden dokument źródłowy w kroku 1, aby wypełnić
              dane.
            </PremiumNotice>
          )}
          {step === 2 && editable && hasDocuments && draft && (
            <>
              {order.status === OrderStatus.READ_FAILED && (
                <PremiumNotice warning>
                  Odczyt nie powiódł się. Możesz wypełnić poniższe pola ręcznie
                  albo zmienić dokumenty w kroku 1 i ponowić odczyt.
                </PremiumNotice>
              )}
              <ReviewIssueContext.Provider
                value={
                  activeSelection && {
                    ...activeSelection,
                    edit: () =>
                      setSelection({ ...activeSelection, editing: true }),
                    dismiss: () => {
                      returnFocus.current = activeSelection.id;
                      setSelection(null);
                    },
                  }
                }
              >
                <ReviewForm
                  key={`${order.id}:${order.revision}`}
                  draft={draft}
                  config={order.config}
                  options={order.plugin?.review}
                  dictionaryChoices={order.dictionaryChoices}
                  dictionaryAccess={{
                    token: accessToken,
                    organizationId: order.organizationId,
                  }}
                  documents={order.documents}
                  disabled={busy}
                  onChange={onChange}
                  onShowSource={showSource}
                />
              </ReviewIssueContext.Provider>
              <OrderActions
                backTo={premiumPaths.orderDocuments(orderId)}
                backLabel="Dokumenty źródłowe"
                dirty={dirty}
                busy={busy}
                save={save}
                onNext={preview}
              />
            </>
          )}
          <OrderHistory events={order.events} documentLabels={documentLabels} />
        </>
      )}
    </PremiumLayout>
  );
}
